import { useState, useEffect } from 'react';
import { eventsApi } from '../../lib/api';

// Lifecycle stages per UI-SPEC Semantic Status Color Map
const LIFECYCLE_STAGES = [
  { key: 'ai_suggested', label: 'AI Suggested', color: '#94A3B8' },
  { key: 'approved', label: 'Approved', color: '#10B981' },
  { key: 'rejected', label: 'Rejected', color: '#F97066' },
  { key: 'nda_sent', label: 'NDA Sent', color: '#4F46E5' },
  { key: 'signed', label: 'Signed', color: '#4F46E5' },
  { key: 'countersigned', label: 'Countersigned', color: '#10B981' },
  { key: 'slot_booked', label: 'Slot Booked', color: '#10B981' },
  { key: 'completed', label: 'Completed', color: '#0F172A' },
];

const cardStyle = {
  background: 'rgba(255,255,255,0.95)',
  border: '1px solid #E2E8F0',
  borderRadius: 20,
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
};

export default function OrganizerStatusDashboard({ eventId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await eventsApi.getOrganizerDashboard(eventId);
        setData(result);
      } catch (err) {
        console.error('Dashboard load error:', err);
      }
      setLoading(false);
    };
    load();
  }, [eventId]);

  if (loading) {
    return (
      <p style={{ fontSize: 14, color: '#94A3B8', fontFamily: 'Poppins, sans-serif' }}>
        Loading dashboard...
      </p>
    );
  }

  if (!data) {
    return (
      <p style={{ fontSize: 14, color: '#F97066', fontFamily: 'Poppins, sans-serif' }}>
        Failed to load dashboard
      </p>
    );
  }

  const { lifecycle_counts = {}, stale_assignments = [], schedule = [] } = data;

  return (
    <div>
      {/* Stat card row — primary visual anchor (D-25, UI-SPEC Organizer Status Dashboard) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 16,
        marginBottom: 32,
      }}>
        {LIFECYCLE_STAGES.map(stage => (
          <div key={stage.key} style={cardStyle}>
            <span style={{
              fontSize: 28,
              fontWeight: 600,
              color: stage.color,
              fontFamily: '"EB Garamond", Georgia, serif',
              lineHeight: 1,
            }}>
              {lifecycle_counts[stage.key] || 0}
            </span>
            <span style={{
              fontSize: 11,
              fontWeight: 600,
              color: stage.color,
              fontFamily: 'Poppins, sans-serif',
            }}>
              {stage.label}
            </span>
          </div>
        ))}
      </div>

      {/* Stale alerts (D-28) */}
      {stale_assignments.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{
            fontSize: 14,
            fontWeight: 600,
            color: '#0F172A',
            fontFamily: 'Poppins, sans-serif',
            marginBottom: 8,
            marginTop: 0,
          }}>
            Stale Assignments
          </h3>
          {stale_assignments.map(sa => (
            <div key={sa.id} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: 8,
              background: '#FFFBEB',
              borderRadius: 8,
              marginBottom: 4,
            }}>
              <span style={{
                background: '#FFFBEB',
                color: '#F59E0B',
                borderRadius: 9999,
                padding: '4px 8px',
                fontSize: 11,
                fontWeight: 600,
                fontFamily: 'Poppins, sans-serif',
                whiteSpace: 'nowrap',
              }}>
                Stale {sa.stale_days}d
              </span>
              <span style={{ fontSize: 14, color: '#0F172A', fontFamily: 'Poppins, sans-serif' }}>
                {sa.lifecycle_status} &mdash; no progress in {sa.stale_days} days
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Visual schedule grid (D-25 — rows = company reps, columns = time sub-slots) */}
      {schedule.length > 0 ? (
        <div>
          <h3 style={{
            fontSize: 14,
            fontWeight: 600,
            color: '#0F172A',
            fontFamily: 'Poppins, sans-serif',
            marginBottom: 8,
            marginTop: 0,
          }}>
            Schedule
          </h3>
          {schedule.map(ch => (
            <div key={ch.id} style={{
              background: 'rgba(255,255,255,0.95)',
              border: '1px solid #E2E8F0',
              borderRadius: 12,
              padding: 12,
              marginBottom: 8,
            }}>
              <div style={{
                fontSize: 14,
                fontWeight: 600,
                color: '#0F172A',
                fontFamily: 'Poppins, sans-serif',
              }}>
                {ch.rep_name || 'Company Rep'}
                {ch.rep_department ? ` \u2014 ${ch.rep_department}` : ''}
              </div>
              <div style={{
                fontSize: 11,
                color: '#94A3B8',
                fontFamily: 'Poppins, sans-serif',
                marginBottom: 8,
              }}>
                {new Date(ch.hour_start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} &mdash;{' '}
                {new Date(ch.hour_end).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
                {(ch.slots || []).map(slot => (
                  <div key={slot.id} style={{
                    padding: '4px 8px',
                    borderRadius: 8,
                    textAlign: 'center',
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: 'Poppins, sans-serif',
                    background: slot.status === 'booked' ? '#ECFDF5' : '#F1F5F9',
                    color: slot.status === 'booked' ? '#10B981' : '#94A3B8',
                    border: `1px solid ${slot.status === 'booked' ? '#10B981' : '#E2E8F0'}`,
                  }}>
                    {new Date(slot.slot_start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    {slot.status === 'booked' ? ' \u2713' : ' \u2014'}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p style={{ fontSize: 14, color: '#94A3B8', fontFamily: 'Poppins, sans-serif' }}>
          No time slots available yet. Slots appear after companies complete their Calendly booking.
        </p>
      )}
    </div>
  );
}
